import { useEffect } from 'react';
import { logger } from '@/lib/logger';
import type { Metric } from 'web-vitals';

interface PerformanceMonitorProps {
  enabled?: boolean;
  onMetric?: (metric: Metric) => void;
}

/**
 * Global performance monitoring component. Tracks Web Vitals when enabled.
 * `web-vitals` is imported dynamically inside the effect so it stays out of the
 * production entry bundle when disabled (it is a no-op in prod today).
 */
export function PerformanceMonitor({
  enabled = import.meta.env.DEV,
  onMetric
}: PerformanceMonitorProps) {
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const handleMetric = (metric: Metric) => {
      logger.info(`Web Vital ${metric.name}:`, {
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
      });
      onMetric?.(metric);
    };

    import('web-vitals').then(({ onCLS, onINP, onLCP, onFCP, onTTFB }) => {
      if (cancelled) return;
      onCLS(handleMetric);
      onINP(handleMetric);
      onLCP(handleMetric);
      onFCP(handleMetric);
      onTTFB(handleMetric);
    });

    return () => { cancelled = true; };
  }, [enabled, onMetric]);

  return null;
}
